/**
 * @file animations.ts
 * @description Centralized animation definitions for the theme system
 */

import { CSSProperties } from 'react';

// Keyframe definitions that will be injected into global CSS
export const keyframes = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(4px);
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slideOut {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(20px);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes scaleOut {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.95);
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.8;
    }
  }

  @keyframes bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-4px);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes dragHandleGlow {
    0% {
      color: #9ca3af;
      transform: scale(1);
    }
    50% {
      color: #3b82f6;
      transform: scale(1.05);
    }
    100% {
      color: #3b82f6;
      transform: scale(1);
    }
  }

  @keyframes ping {
    75%, 100% {
      transform: scale(2);
      opacity: 0;
    }
  }
`;

// Animation style objects
export const animations = {
  fadeIn: {
    animation: 'fadeIn 0.3s ease-out',
  } as CSSProperties,
  
  fadeOut: {
    animation: 'fadeOut 0.3s ease-out',
  } as CSSProperties,
  
  slideIn: {
    animation: 'slideIn 0.3s ease-out',
  } as CSSProperties,
  
  slideOut: {
    animation: 'slideOut 0.3s ease-out',
  } as CSSProperties,
  
  slideUp: {
    animation: 'slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  } as CSSProperties,
  
  slideDown: {
    animation: 'slideDown 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  } as CSSProperties,
  
  scaleIn: {
    animation: 'scaleIn 0.3s ease-out',
  } as CSSProperties,
  
  scaleOut: {
    animation: 'scaleOut 0.3s ease-out',
  } as CSSProperties,
  
  spin: {
    animation: 'spin 1s linear infinite',
  } as CSSProperties,
  
  spinSlow: {
    animation: 'spin 3s linear infinite',
  } as CSSProperties,
  
  spinFast: {
    animation: 'spin 0.5s linear infinite',
  } as CSSProperties,
  
  pulse: {
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  } as CSSProperties,
  
  pulseFast: {
    animation: 'pulse 1s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  } as CSSProperties,
  
  bounce: {
    animation: 'bounce 0.6s ease-in-out',
  } as CSSProperties,
  
  bounceInfinite: {
    animation: 'bounce 1s ease-in-out infinite',
  } as CSSProperties,
  
  shimmer: {
    background: 'linear-gradient(90deg, var(--color-gray-200) 25%, var(--color-gray-100) 50%, var(--color-gray-200) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  } as CSSProperties,
  
  ping: {
    animation: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
  } as CSSProperties,
  
  rotate: {
    animation: 'rotate 1s linear infinite',
  } as CSSProperties,

  dragHandleGlow: {
    animation: 'dragHandleGlow 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards',
  } as CSSProperties,
};

// Hover effect styles
export const hoverEffects = {
  lift: {
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--shadow-lg)',
    },
  } as CSSProperties,
  
  scale: {
    transition: 'transform 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  } as CSSProperties,
  
  scaleSmall: {
    transition: 'transform 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.02)',
    },
  } as CSSProperties,
  
  scaleLarge: {
    transition: 'transform 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.1)',
    },
  } as CSSProperties,
  
  rotate: {
    transition: 'transform 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'rotate(90deg)',
    },
  } as CSSProperties,
  
  opacity: {
    transition: 'opacity 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  } as CSSProperties,
  
  shadow: {
    transition: 'box-shadow 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: 'var(--shadow-xl)',
    },
  } as CSSProperties,
  
  glow: {
    transition: 'box-shadow 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 0 20px rgba(105, 101, 219, 0.4)',
    },
  } as CSSProperties,
};

// Transition utilities
export const transitions = {
  all: {
    transition: 'all var(--transition-base)',
  } as CSSProperties,
  
  fast: {
    transition: 'all var(--transition-fast)',
  } as CSSProperties,
  
  slow: {
    transition: 'all var(--transition-slow)',
  } as CSSProperties,
  
  none: {
    transition: 'none',
  } as CSSProperties,
  
  opacity: {
    transition: 'opacity var(--transition-base)',
  } as CSSProperties,
  
  transform: {
    transition: 'transform var(--transition-base)',
  } as CSSProperties,
  
  colors: {
    transition: 'background-color var(--transition-base), border-color var(--transition-base), color var(--transition-base)',
  } as CSSProperties,
  
  shadow: {
    transition: 'box-shadow var(--transition-base)',
  } as CSSProperties,
  
  width: {
    transition: 'width var(--transition-base)',
  } as CSSProperties,
  
  height: {
    transition: 'height var(--transition-base)',
  } as CSSProperties,
};

// Stagger animation utilities for lists
export const staggerChildren = (baseDelay: number = 0.05): CSSProperties => ({
  '& > *': {
    opacity: 0,
    animation: 'fadeIn 0.3s ease-out forwards',
  },
  '& > *:nth-child(1)': { animationDelay: `${baseDelay}s` },
  '& > *:nth-child(2)': { animationDelay: `${baseDelay * 2}s` },
  '& > *:nth-child(3)': { animationDelay: `${baseDelay * 3}s` },
  '& > *:nth-child(4)': { animationDelay: `${baseDelay * 4}s` },
  '& > *:nth-child(5)': { animationDelay: `${baseDelay * 5}s` },
  '& > *:nth-child(6)': { animationDelay: `${baseDelay * 6}s` },
  '& > *:nth-child(7)': { animationDelay: `${baseDelay * 7}s` },
  '& > *:nth-child(8)': { animationDelay: `${baseDelay * 8}s` },
  '& > *:nth-child(9)': { animationDelay: `${baseDelay * 9}s` },
  '& > *:nth-child(10)': { animationDelay: `${baseDelay * 10}s` },
} as CSSProperties);

// Micro interaction styles
export const microInteraction = {
  ripple: {
    position: 'relative' as const,
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: 0,
      height: 0,
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.3)',
      transform: 'translate(-50%, -50%)',
      transition: 'width 0.4s ease, height 0.4s ease',
    },
    '&:active::after': {
      width: '200px',
      height: '200px',
    },
  } as CSSProperties,
};

// Skeleton loading styles
export const skeleton = {
  loading: {
    background: 'linear-gradient(90deg, var(--color-gray-200) 25%, var(--color-gray-100) 50%, var(--color-gray-200) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: 'var(--radius-md)',
  } as CSSProperties,
  
  text: {
    height: '1em',
    marginBottom: '0.5em',
    borderRadius: 'var(--radius-sm)',
  } as CSSProperties,
  
  title: {
    height: '1.5em',
    marginBottom: '0.75em',
    borderRadius: 'var(--radius-sm)',
  } as CSSProperties,
  
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: 'var(--radius-full)',
  } as CSSProperties,
  
  thumbnail: {
    width: '100px',
    height: '100px',
    borderRadius: 'var(--radius-lg)',
  } as CSSProperties,
  
  card: {
    height: '200px',
    borderRadius: 'var(--radius-lg)',
  } as CSSProperties,
};

// Inject keyframes into document head
export const injectAnimations = () => {
  const styleId = 'focus-hub-animations';
  
  // Check if already injected
  if (document.getElementById(styleId)) {
    return;
  }
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = keyframes;
  document.head.appendChild(style);
};

// Reduced motion utilities
export const reducedMotion = (window.matchMedia('(prefers-reduced-motion: reduce)').matches);

export const getAnimation = (animation: CSSProperties): CSSProperties => {
  if (reducedMotion) {
    return {
      ...animation,
      animationDuration: '0.01ms',
      animationIterationCount: '1',
      transitionDuration: '0.01ms',
    };
  }
  return animation;
};